import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

const zodUpdateDirectMappingSchema = z.object({
  accRefs: z.array(z.uuid()),
  replaceAfterSigning: z.boolean().optional(),
  replaceCcFields: z.boolean().optional(),
  replacePrFields: z.boolean().optional(),
  sites: z.array(z.uuid()),
});
const zodUpdateDirectMappingEndpointSchema = z.tuple([
  z.literal('asset'),
  z.uuid(), // deliveryNoteId
  z.literal('direct_mapping'),
]);

type MutationDirectMappingPayload = z.infer<
  typeof zodUpdateDirectMappingSchema
>;

type MutationDirectMappingArguments = {
  deliveryNoteId: UUID;
  payload: MutationDirectMappingPayload;
};

/**
 * Updates a given site.
 *
 * @param {string} deliveryNoteId - The ID of the delivery note to map.
 * @param {Object} payload - An object containing the delivery note map data to update.
 * @returns {Promise} A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note/operation/update_direct_dln_mapping_asset__id__direct_mapping_put
 */
export const updateDirectMapping = async (
  deliveryNoteId: UUID,
  payload: MutationDirectMappingPayload,
) => {
  const response = await vestigasApi
    .put(ENDPOINT.DELIVERY_NOTE.UPDATE_DIRECT_MAPPING(deliveryNoteId), {
      json: payload,
      validate: {
        body: zodUpdateDirectMappingSchema,
        pathSegments: zodUpdateDirectMappingEndpointSchema,
      },
    })
    .json();

  return response;
};

/**
 * Custom hook for updating a given site.
 *
 * @returns {Object} An object containing the mutation function and related properties.
 */
export const useMutationUpdateDirectMapping = (
  options?: Parameters<typeof useMutation>[0],
) => {
  return useMutation({
    mutationFn: async ({
      deliveryNoteId,
      payload,
    }: MutationDirectMappingArguments) =>
      updateDirectMapping(deliveryNoteId, payload),
    ...options,
  });
};
